import path from "path";
import { readSecretsFile, writeSecretsFile, SecretsFile } from "./file-format";
import { encryptSecret, decryptSecret } from "./crypto";

// --- Real encryption/decryption logic ---
export async function decryptSecretsFile(
  filePath: string,
  identity: string
): Promise<Record<string, string>> {
  const secretsFile: SecretsFile = await readSecretsFile(filePath);
  const result: Record<string, string> = {};
  for (const [key, entry] of Object.entries(secretsFile.secrets)) {
    const armored = typeof entry === "string" ? entry : entry.value;
    try {
      result[key] = await decryptSecret(armored, identity);
    } catch (e) {
      // Optionally: skip or throw
      throw new Error(
        `Failed to decrypt secret '${key}' in ${filePath}: ${
          e instanceof Error ? e.message : e
        }`
      );
    }
  }
  return result;
}

export async function encryptSecretsFile(
  secrets: Record<string, string>,
  outputPath: string,
  recipients: string[]
): Promise<void> {
  // Write as a new SecretsFile
  const secretsFile: SecretsFile = {
    version: 1,
    secrets: {},
  };
  for (const [key, value] of Object.entries(secrets)) {
    secretsFile.secrets[key] = await encryptSecret(value, recipients);
  }
  await writeSecretsFile(outputPath, secretsFile);
}

// --- Merge logic ---
export function mergeSecrets(
  base: Record<string, string>,
  ours: Record<string, string>,
  theirs: Record<string, string>
): {
  merged: Record<string, string>;
  conflicts: Record<string, { ours: string; theirs: string }>;
} {
  const merged: Record<string, string> = {};
  const conflicts: Record<string, { ours: string; theirs: string }> = {};
  const allKeys = new Set([
    ...Object.keys(base),
    ...Object.keys(ours),
    ...Object.keys(theirs),
  ]);
  for (const key of allKeys) {
    const b = base[key];
    const o = ours[key];
    const t = theirs[key];
    if (o === t) {
      merged[key] = o;
    } else if (b === o) {
      merged[key] = t;
    } else if (b === t) {
      merged[key] = o;
    } else if (o !== t) {
      // Conflict
      conflicts[key] = { ours: o, theirs: t };
      merged[key] = `<<<<<<< ours\n${o}\n=======\n${t}\n>>>>>>> theirs`;
    }
  }
  return { merged, conflicts };
}

// --- CLI handler ---
export async function mergeSecretsCommand({
  base,
  ours,
  theirs,
  output,
  identity,
  recipients = [],
}: {
  base: string;
  ours: string;
  theirs: string;
  output: string;
  identity: string;
  recipients?: string[];
}) {
  // Decrypt all three files
  const baseSecrets = await decryptSecretsFile(base, identity);
  const ourSecrets = await decryptSecretsFile(ours, identity);
  const theirSecrets = await decryptSecretsFile(theirs, identity);
  // Merge
  const { merged, conflicts } = mergeSecrets(
    baseSecrets,
    ourSecrets,
    theirSecrets
  );
  // Remove keys with undefined values (i.e., deleted in both ours and theirs)
  const filteredMerged = Object.fromEntries(
    Object.entries(merged).filter(([_, v]) => v !== undefined)
  );
  // Print summary
  const conflictKeys = Object.keys(conflicts);
  if (conflictKeys.length) {
    console.log("Conflicts detected:");
    for (const key of conflictKeys) {
      console.log(`  ${key}`);
    }
  } else {
    console.log("No conflicts. All secrets merged cleanly.");
  }
  // Write merged file
  if (Object.keys(filteredMerged).length === 0) {
    console.log("No secrets to write. Merged output will not be created.");
    return;
  }
  await encryptSecretsFile(filteredMerged, output, recipients);
  console.log(`Merged secrets written to ${output}`);
}
